// Booked calls (owner request, PJ, 2026-10-06; migration 0027): the call that produced a meeting, played and read by the
// whole team. One panel used by the Meetings page (expanded row, ?open=<id>) and the Booked calls page.
import '../dialer/dialer.css';
import { useState } from 'preact/hooks';
import type { BookedCallDetail, BookedCallSummary, CloserBrief } from '../../../src/shared/api-booked-calls';
import { errorMessage } from '../api';
import { endpoints } from '../dialer/endpoints';
import { DISPOSITION_META } from '../dialer/labels';
import { fmtDateTime } from './ui';
import { Spinner } from './ui-extra';

/** "4:12" (or '—'). */
export function mmss(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || !Number.isFinite(sec)) return '—';
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function outcomeLabel(code: BookedCallSummary['outcome']): string {
  return code ? DISPOSITION_META[code]?.label ?? code : 'not logged';
}

/** Why there's no brief yet, in one sentence. The recording's own sentence ("Not recorded: ...") is shown above it. */
export function briefStatusText(b: BookedCallSummary): string {
  switch (b.briefStatus) {
    case 'ready': return '';
    case 'no_call': return "No brief: this booking isn't tied to a call in the dialer.";
    // The stored reason, not a fixed sentence (review 2026-10-20): since 2026-10-20 it is "the setter left no notes ...",
    // but a meeting marked not recorded before then kept its old reason (why it wasn't recorded) and may have notes.
    case 'not_recorded': return `No closer brief: ${b.briefError ?? "the call wasn't recorded."}`;
    case 'failed': return `The brief couldn't be written${b.briefError ? `: ${b.briefError}` : '.'}`;
    default:
      if (b.recording === 'pending') return 'The brief is written once the recording is filed (usually a minute or two after the call).';
      if (b.briefError) return `Brief not written yet: ${b.briefError}`;
      // PJ 2026-10-20: an unrecorded call's brief comes from the setter's notes, once they have settled.
      if (b.recording === 'not_recorded') return "The brief is written from the setter's notes about 10 minutes after the call (it wasn't recorded).";
      return 'The brief is being written (usually within a few minutes of the booking).';
  }
}

function List(props: { items: string[]; empty: string }) {
  if (!props.items.length) return <div class="muted" style="font-size:13px">{props.empty}</div>;
  return <ul class="vd-brief-list">{props.items.map((x) => <li>{x}</li>)}</ul>;
}

export function BriefView(props: { brief: CloserBrief }) {
  const b = props.brief;
  return (
    <div class="vd-brief">
      {b.source === 'notes' ? <div class="vd-brief-wide"><span class="chip chip-warn">From the setter's notes, no recording</span></div> : null}
      <div><span class="eyebrow">Spoke with</span><div>{b.spokeWith}</div></div>
      <div><span class="eyebrow">Cares about</span><List items={b.caresAbout} empty="not mentioned" /></div>
      <div><span class="eyebrow">Phones today</span><div>{b.phones}</div></div>
      <div><span class="eyebrow">Booking today</span><div>{b.booking}</div></div>
      <div class="vd-brief-wide"><span class="eyebrow">Objections and how they were handled</span>
        {b.objections.length
          ? <ul class="vd-brief-list">{b.objections.map((o) => <li><b>{o.objection}</b> → {o.handled}</li>)}</ul>
          : <div class="muted" style="font-size:13px">none raised</div>}
      </div>
      <div><span class="eyebrow">Setter promised</span><List items={b.promised} empty="nothing" /></div>
      <div><span class="eyebrow">Prepare before the meeting</span><List items={b.prepare} empty="not mentioned" /></div>
    </div>
  );
}

function Transcript(props: { meetingId: number }) {
  const [state, setState] = useState<{ loading: boolean; data: BookedCallDetail | null; error: string | null }>({ loading: false, data: null, error: null });
  const load = async () => {
    if (state.loading || state.data) return;
    setState({ loading: true, data: null, error: null });
    try {
      setState({ loading: false, data: await endpoints.bookedCall(props.meetingId), error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: errorMessage(e) });
    }
  };
  const t = state.data?.transcript ?? null;
  return (
    <details class="vd-transcript" onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open) void load(); }}>
      <summary>Transcript</summary>
      {state.loading ? <div class="muted"><Spinner /> Loading the transcript…</div> : null}
      {state.error ? <div class="vd-bad">Couldn't load it: {state.error}</div> : null}
      {state.data && !t ? <div class="muted">No transcript yet.</div> : null}
      {t ? (
        <div class="vd-turns">
          {!t.labelled ? <div class="vd-field-hint">No speaker labels: the setter and the prospect are in one block (the transcription hears both sides together).</div> : null}
          {t.turns.map((turn) => (
            <p class={turn.speaker === 'Valistix' ? 'vd-turn vd-turn-us' : turn.speaker === 'Prospect' ? 'vd-turn vd-turn-them' : 'vd-turn'}>
              {turn.speaker ? <b>{turn.speaker === 'Valistix' ? 'Setter' : 'Prospect'}: </b> : null}{turn.text}
            </p>
          ))}
        </div>
      ) : null}
    </details>
  );
}

/** Admin: write a brief that gave up again (after the key or the credit was fixed). */
function RetryBrief(props: { meetingId: number; onDone: (c: BookedCallSummary | null) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    setBusy(true); setError(null);
    try {
      const d = await endpoints.retryBookedCallBrief(props.meetingId);
      props.onDone(d.bookedCall);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <span class="row" style="gap:8px;align-items:center;flex-wrap:wrap">
      <button class="btn btn-sm" disabled={busy} onClick={go}>{busy ? <><Spinner /> Writing the brief…</> : 'Write the brief again'}</button>
      {error ? <span class="vd-bad" style="font-size:13px">{error}</span> : null}
    </span>
  );
}

/** Player + closer brief + transcript for one meeting's booked call. canRetry (admins): a "Write the brief again" button
 *  on a brief that gave up; onChanged is called after it (the page reloads its list). */
export function BookedCallPanel(props: { meetingId: number; call: BookedCallSummary; showDiscord?: boolean; canRetry?: boolean; onChanged?: () => void }) {
  const [fresh, setFresh] = useState<BookedCallSummary | null>(null);
  const c = fresh ?? props.call;
  return (
    <div class="vd-booked stack" style="gap:10px">
      <div class="row" style="gap:10px;flex-wrap:wrap;align-items:center">
        <span class="eyebrow">Booked call</span>
        <span class="muted" style="font-size:13px">{fmtDateTime(c.startedAt)} · {mmss(c.durationSec)} · {outcomeLabel(c.outcome)}</span>
        {props.showDiscord ? (
          c.discordPostedAt ? <span class="chip chip-ok" title={fmtDateTime(c.discordPostedAt)}>Posted to Discord</span>
            : c.discordError ? <span class="chip chip-bad" title={c.discordError}>Discord failed</span> : null
        ) : null}
      </div>
      {c.playUrl
        ? <audio controls preload="none" src={c.playUrl} style="width:100%;max-width:560px" />
        : <div class={c.recording === 'pending' ? 'muted' : 'vd-note'}>{c.recordingNote}</div>}
      {c.brief ? <BriefView brief={c.brief} /> : <div class="muted" style="font-size:13px">{briefStatusText(c)}</div>}
      {!c.brief && c.briefStatus === 'failed' && props.canRetry
        ? <RetryBrief meetingId={props.meetingId} onDone={(next) => { if (next) setFresh(next); props.onChanged?.(); }} /> : null}
      {c.hasTranscript ? <Transcript meetingId={props.meetingId} /> : null}
    </div>
  );
}
